import { memo, useEffect, useRef, useState } from 'react'
import {
  checkInviteEmail,
  isCompleteEmail,
  searchStudioPeople,
  sortStudioPeopleHits,
  type StudioPeopleHit,
} from './api/studioPeopleSearch'
import { ScenariosEventTypes } from '../platform/analytics/analytics'
import { personUserId } from './pagePeopleModel'
import { trackScenariosEvent } from './scenariosAnalytics'
import type { ScenarioPerson } from './types'

/** Roster group header. Keeps the official tenant name; portal gets a public-users gloss. */
function rosterTenantLabel(name: string | null | undefined, fallback: string): string {
  const raw = name?.trim() || ''
  if (!raw) return fallback
  if (/^portal$/i.test(raw)) return 'portal (public users)'
  return raw
}

/**
 * Roster add field. Owns the query so each keystroke does not re-render ScenariosPage
 * (map, clocks, resolveSession). Parent only hears a pick.
 */
export const RosterAddField = memo(function RosterAddField({
  homeTenantId,
  activePeople,
  onPick,
}: {
  homeTenantId: string | null
  activePeople: readonly ScenarioPerson[]
  onPick: (row: StudioPeopleHit) => void
}) {
  const [query, setQuery] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [hits, setHits] = useState<StudioPeopleHit[]>([])
  const [busy, setBusy] = useState(false)
  const [hint, setHint] = useState<string | null>(null)
  const pickingRef = useRef(false)
  const searchTimerRef = useRef<number | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)

  function setMenu(open: boolean): void {
    setMenuOpen(open)
  }

  function clearEntry(): void {
    setQuery('')
    setMenu(false)
    setHighlight(0)
    setHint(null)
    setHits([])
    setBusy(false)
  }

  useEffect(() => {
    const q = query.trim()
    if (searchTimerRef.current != null) {
      window.clearTimeout(searchTimerRef.current)
      searchTimerRef.current = null
    }
    searchAbortRef.current?.abort()
    searchAbortRef.current = null
    if (q.length < 3) {
      setHits([])
      setBusy(false)
      return
    }
    setBusy(true)
    searchTimerRef.current = window.setTimeout(() => {
      const ac = new AbortController()
      searchAbortRef.current = ac
      void searchStudioPeople(q, ac.signal)
        .then(async (rows) => {
          if (ac.signal.aborted) return
          let next = sortStudioPeopleHits(rows, homeTenantId)
          const exact = q.toLowerCase()
          if (isCompleteEmail(q) && !next.some((row) => row.email.toLowerCase() === exact)) {
            try {
              const check = await checkInviteEmail(q, ac.signal)
              if (ac.signal.aborted) return
              if (check.result === 'absent') {
                setHint(null)
                next = [
                  {
                    inviteAsNew: true,
                    userId: '',
                    email: q.trim(),
                    name: 'Invite as a new account',
                    tenantId: check.tenantId,
                    tenantName: check.tenantName,
                  },
                  ...next,
                ]
              } else if (check.result === 'eligible' && check.hit) {
                setHint(null)
                const already = next.some(
                  (row) => row.email.toLowerCase() === check.hit!.email.toLowerCase(),
                )
                if (!already) next = [check.hit, ...next]
              } else if (check.reason === 'org-not-ready') {
                trackScenariosEvent(ScenariosEventTypes.INVITE_CHECK_FAILED, {
                  reason: check.reason,
                  targetTenantId: check.tenantId,
                  targetTenantName: check.tenantName,
                })
                setHint('This organization is not ready for Scenario Studio accounts yet.')
              } else if (check.reason === 'sso') {
                trackScenariosEvent(ScenariosEventTypes.INVITE_CHECK_FAILED, {
                  reason: check.reason,
                  targetTenantId: check.tenantId,
                  targetTenantName: check.tenantName,
                })
                setHint(
                  'This company email uses single sign-on. Sign in with your company account instead of creating a password.',
                )
              } else {
                trackScenariosEvent(ScenariosEventTypes.INVITE_CHECK_FAILED, {
                  reason: check.reason ?? check.result,
                  targetTenantId: check.tenantId,
                  targetTenantName: check.tenantName,
                })
                setHint("That email can't be added as a new account.")
              }
            } catch {
              if (!ac.signal.aborted) setHint(null)
            }
          } else if (!ac.signal.aborted) {
            setHint(null)
          }
          if (!ac.signal.aborted) setHits(next)
        })
        .catch(() => {
          if (ac.signal.aborted) return
          setHits([])
        })
        .finally(() => {
          if (!ac.signal.aborted) setBusy(false)
        })
    }, 280)
    return () => {
      if (searchTimerRef.current != null) {
        window.clearTimeout(searchTimerRef.current)
        searchTimerRef.current = null
      }
      searchAbortRef.current?.abort()
    }
  }, [query, homeTenantId])

  const q = query.trim()
  const homeKey = (homeTenantId ?? '').toLowerCase()
  const onRoster = new Set(
    activePeople.map((person) => person.email.trim().toLowerCase()).filter(Boolean),
  )
  const onRosterIds = new Set(activePeople.map((person) => personUserId(person)).filter(Boolean))
  const matched =
    q.length >= 3
      ? hits.filter((row) => {
          const email = row.email.trim().toLowerCase()
          const userId = row.userId.trim()
          if (!email) return false
          if (row.inviteAsNew) return !onRoster.has(email)
          if (!userId) return false
          if (onRoster.has(email) || onRosterIds.has(userId)) return false
          return true
        })
      : []
  const inviteRows = matched.filter((row) => row.inviteAsNew)
  const accountRows = matched
    .filter((row) => !row.inviteAsNew)
    .sort((a, b) => {
      const ah = (a.tenantId ?? '').toLowerCase() === homeKey ? 0 : 1
      const bh = (b.tenantId ?? '').toLowerCase() === homeKey ? 0 : 1
      if (ah !== bh) return ah - bh
      const byTenant = (a.tenantName ?? '').localeCompare(b.tenantName ?? '', undefined, {
        sensitivity: 'base',
      })
      if (byTenant !== 0) return byTenant
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  const shown = [...inviteRows, ...accountRows].slice(0, 10)
  const alreadyOnRoster =
    isCompleteEmail(q) &&
    activePeople.some((person) => person.email.trim().toLowerCase() === q.toLowerCase())
  const rosterStatus =
    hint && q.length >= 3 && !shown.length
      ? hint
      : q.length > 0 && q.length < 3
        ? 'Type at least 3 characters to search.'
        : busy && q.length >= 3 && !shown.length
          ? 'Searching…'
          : alreadyOnRoster && !shown.length
            ? 'Already on this roster.'
            : q.length >= 3 && !busy && !shown.length
              ? 'No matching users.'
              : ''
  const hi = shown.length === 0 ? 0 : Math.min(Math.max(highlight, 0), shown.length - 1)
  const listOpen = menuOpen && shown.length > 0

  function pick(row: StudioPeopleHit): void {
    pickingRef.current = false
    onPick(row)
    clearEntry()
  }

  function submit(): void {
    if (shown.length) {
      const row = shown[hi]
      if (row) {
        pick(row)
        return
      }
    }
    if (q.length >= 3) {
      const key = q.toLowerCase()
      const exact = matched.find(
        (row) => row.email.toLowerCase() === key || row.name.toLowerCase() === key,
      )
      const only = matched.length === 1 ? matched[0] : null
      const chosen = exact ?? only
      if (chosen) {
        pick(chosen)
        return
      }
    }
    clearEntry()
  }

  return (
    <form
      className="sc-people-typeahead"
      autoComplete="off"
      data-1p-ignore
      data-lpignore="true"
      data-bwignore
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="sc-roster-field">
        <input
          className="sc-fin"
          placeholder="Add by name or email…"
          value={query}
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          data-protonpass-ignore="true"
          aria-label="Add to roster"
          aria-autocomplete="list"
          aria-controls="sc-roster-menu"
          aria-expanded={listOpen}
          aria-activedescendant={listOpen && shown[hi] ? `sc-roster-opt-${hi}` : undefined}
          onFocus={() => {
            if (query.trim()) setMenu(true)
          }}
          onBlur={() => {
            if (pickingRef.current) return
            setMenu(false)
          }}
          onChange={(e) => {
            const next = e.target.value
            setQuery(next)
            setMenu(Boolean(next.trim()))
            setHighlight(0)
            setHint(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              clearEntry()
              return
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              e.stopPropagation()
              if (!shown.length) return
              setMenu(true)
              setHighlight((cur) => Math.min(cur + 1, shown.length - 1))
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              e.stopPropagation()
              if (!shown.length) return
              setMenu(true)
              setHighlight((cur) => Math.max(cur - 1, 0))
            }
          }}
        />
        {listOpen ? (
          <ul id="sc-roster-menu" className="sc-roster-menu sc-roster-menu-up" role="listbox">
            {shown.map((row, i) => {
              const prev = i > 0 ? shown[i - 1] : null
              const tenantLabel = row.inviteAsNew
                ? rosterTenantLabel(row.tenantName, 'Invite as new Buxus user')
                : rosterTenantLabel(row.tenantName, 'Unknown organization')
              const prevLabel = prev
                ? prev.inviteAsNew
                  ? rosterTenantLabel(prev.tenantName, 'Invite as new Buxus user')
                  : rosterTenantLabel(prev.tenantName, 'Unknown organization')
                : ''
              const showTenantHead = Boolean(tenantLabel) && tenantLabel !== prevLabel
              return (
                <li
                  key={`${row.inviteAsNew ? 'new' : row.userId}:${row.email.toLowerCase()}`}
                  id={`sc-roster-opt-${i}`}
                  role="option"
                  aria-selected={i === hi}
                >
                  {showTenantHead ? (
                    <div className="sc-roster-menu-group">{tenantLabel}</div>
                  ) : null}
                  <button
                    type="button"
                    className={i === hi ? 'is-active' : undefined}
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      pickingRef.current = true
                      pick(row)
                    }}
                    onMouseEnter={() => setHighlight(i)}
                  >
                    <>
                      <b>{row.inviteAsNew ? 'Invite as a new account' : row.name}</b>
                      <span>{row.email}</span>
                    </>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : rosterStatus ? (
          <p className="sc-roster-note" role="status">
            {rosterStatus}
          </p>
        ) : null}
      </div>
    </form>
  )
})
